import { useMutation, useQueryClient } from "react-query";
import { createApiWithPath } from "../../client";

interface BulkFieldValue {
  id?: string;
  field_type_id: string;
  value: any;
  sort_order?: number;
  parent_id?: string | null;
}

interface BulkUpsertRequest {
  profile_id: string;
  fields: BulkFieldValue[];
  replace_mode: boolean;
}

interface BulkUpsertResponse {
  data: {
    created: number;
    updated: number;
    errors: Array<{
      index: number;
      field_type_id: string | null;
      error: string;
    }>;
    total: number;
  };
}

export const useBulkUpsertFieldValues = () => {
  const queryClient = useQueryClient();

  return useMutation<BulkUpsertResponse, Error, BulkUpsertRequest>(
    async (data) => {
      const response = await createApiWithPath("crm").post<BulkUpsertResponse>(
        "/field-values/bulk",
        data,
      );
      const responseData: any = response?.data;
      if (responseData && responseData.success === false) {
        throw new Error(
          responseData.message || "Bulk field values update failed",
        );
      }
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate all related queries
        queryClient.invalidateQueries(["profile-fields", variables.profile_id]);
        queryClient.invalidateQueries(["people", variables.profile_id]);
      },
      onError: (error) => {
        console.error("Bulk field values update failed:", error);
      },
    },
  );
};

export const prepareBulkFieldValues = (
  profileId: string,
  fieldValues: Record<string, any>,
  originalValues: Record<string, any> = {},
  replaceMode: boolean = false,
): BulkUpsertRequest => {
  const fields: BulkFieldValue[] = [];

  Object.entries(fieldValues).forEach(([fieldTypeId, value]) => {
    if (originalValues[fieldTypeId] === value) {
      return;
    }

    // Include empty fields so backend can delete existing values
    // The backend will handle removal of field values when value is empty
    fields.push({
      field_type_id: fieldTypeId,
      value: value,
    });
  });

  return {
    profile_id: profileId,
    fields,
    replace_mode: replaceMode,
  };
};
